import { useId } from 'react'
import { site } from '@/config/site'
import { leadership } from '@/config/leadership'
import { Picture } from '@/components/ui/Picture'

/**
 * About — Website Review V2: "Who We Are" retitled to About, the wordmark set
 * over two lines, Mission styled to match Vision, leadership sourced from the
 * client slide, and the Advisory Council shown as in assembly.
 */
export function About() {
  const headingId = useId()

  return (
    <section
      id="about"
      aria-labelledby={headingId}
      className="bg-surface py-section-y"
    >
      <div className="mx-auto max-w-[88rem] px-6 sm:px-10">
        <div className="grid grid-cols-1 gap-x-12 gap-y-10 lg:grid-cols-12">
          <header className="lg:col-span-5">
            <span className="eyebrow">About</span>
            <h2
              id={headingId}
              className="mt-4 font-display text-display-2 font-extrabold leading-[0.95] text-ink-deep"
            >
              GIA Longevity
              <span className="block text-ink-slate">U.S. Chapter</span>
            </h2>
          </header>

          <div className="space-y-8 lg:col-span-7">
            <p className="font-display text-2xl font-bold leading-snug text-ink-deep sm:text-3xl">
              The Global Initiative on Ageing and Longevity (GIA Longevity),
              a global nonprofit alliance addressing the ageing and longevity
              ecosystem, is co-designing a new society that empowers everyone
              to thrive throughout their entire lifespan.
            </p>

            <dl className="space-y-6 border-l-4 border-brand pl-6">
              <div>
                <dt className="eyebrow">Vision</dt>
                <dd className="mt-2 font-display text-xl font-extrabold text-ink-deep sm:text-2xl">
                  {site.vision}
                </dd>
              </div>
              <div>
                <dt className="eyebrow">Mission</dt>
                <dd className="mt-2 font-display text-xl font-extrabold text-ink-deep sm:text-2xl">
                  {site.mission}
                </dd>
              </div>
            </dl>

            <p className="measure text-base leading-relaxed text-ink-slate sm:text-lg">
              GIA Longevity is where the world&rsquo;s most forward-thinking
              leaders &mdash; from business, policy, science, and
              philanthropy &mdash; come together to build the longevity
              ecosystem society urgently needs. Your investment funds the
              connective tissue of a longer-lived world.
            </p>
          </div>
        </div>

        {/* Leadership — sourced from the client slide */}
        <div className="mt-20 border-t border-hairline pt-12">
          <div className="grid grid-cols-1 gap-x-12 gap-y-8 lg:grid-cols-12">
            <header className="lg:col-span-4">
              <span className="eyebrow">Our team</span>
              <h3 className="mt-3 font-display text-display-3 font-extrabold text-ink-deep">
                Leadership
              </h3>
              <p className="mt-3 max-w-sm text-sm leading-relaxed text-ink-slate">
                The people leading the U.S. Chapter.
              </p>
            </header>

            <div className="lg:col-span-8">
              <ul className="grid grid-cols-1 gap-x-8 gap-y-10 sm:grid-cols-2">
                {leadership.map((leader) => (
                  <li key={leader.id} className="flex min-w-0 items-center gap-4">
                    <div className="h-16 w-16 shrink-0 overflow-hidden rounded-full bg-surface-alt">
                      <Picture
                        name={leader.photo}
                        sizes="64px"
                        alt={`Portrait of ${leader.name}`}
                        className="h-full w-full object-cover"
                      />
                    </div>
                    <div className="min-w-0">
                      <p className="break-words font-display text-lg font-extrabold leading-tight text-ink-deep">
                        {leader.name}
                      </p>
                      <p className="mt-1 text-sm leading-snug text-ink-slate">
                        {leader.role}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Advisory Council — in assembly */}
        <div className="mt-16 border-t border-hairline pt-12">
          <div className="grid grid-cols-1 gap-x-12 gap-y-8 lg:grid-cols-12">
            <header className="lg:col-span-4">
              <span className="eyebrow">Coalition</span>
              <h3 className="mt-3 font-display text-display-3 font-extrabold text-ink-deep">
                Advisory Council
              </h3>
              <p className="mt-3 max-w-sm text-sm leading-relaxed text-ink-slate">
                Leaders from across sectors shaping the work from the inside.
              </p>
            </header>

            <div className="lg:col-span-8">
              <p className="border-l-4 border-brand pl-4 text-sm leading-relaxed text-ink-slate">
                The Advisory Council is in assembly. Members will be announced
                as they formally join.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
